import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAAssinatura, CadernoLPMAPlantioOutraCultura } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';
import api from '../../services/api';
import AssinaturaComNomesAdicionais, {
	parseNomesEnvolvidosJson,
	stringifyNomesEnvolvidos,
} from './components/AssinaturaComNomesAdicionais';
import MetodoLimpezaSelect from './components/MetodoLimpezaSelect';
import {
	formatarMetodoLimpezaParaJson,
	normalizarMetodoLimpezaParaFormulario,
} from '../../util/cadernoLPMA/metodoLimpeza';

type PlantioOutraCulturaForm = CadernoLPMAPlantioOutraCultura & {
	assinaturaEnvolvidosBase64?: string | null;
	nomesEnvolvidosAdicionaisJson?: string | null;
};

interface PlantioOutraCulturaModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAPlantioOutraCultura) => void;
	dadosPlantioOutraCultura?: PlantioOutraCulturaForm | null;
	cadernoLpmaId?: number | null;
	assinaturasDisponiveis?: CadernoLPMAAssinatura[];
}

const defaultValues: PlantioOutraCulturaForm = {
	dataInicioPlantioOutraCultura: null,
	dataTerminoPlantioOutraCultura: null,
	talhaoPlantioOutraCultura: '',
	culturaPlantadaPlantioOutraCultura: '',
	responsavelPlantioOutraCultura: '',
	previsaoLPMA: '',
	dataAutorizacao: null,
	plantioOutraCulturaJson: '',
	limpezaPlantioOutraCulturaJson: '',
	observacoesPlantioOutraCultura: '',
	assinaturaEnvolvidosBase64: null,
	nomesEnvolvidosAdicionaisJson: null,
	comunicacaoAutorizacaoNomeArquivo: null,
	comunicacaoAutorizacaoContentType: null,
	comunicacaoAutorizacaoCaminhoBlob: null,
	comunicacaoAutorizacaoConteudoBase64: null,
	removerComunicacaoAutorizacaoAnexo: null,
};

const MIME_PDF = 'application/pdf';
const MIME_DOCX = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const arquivoParaBase64 = (arquivo: File): Promise<string> =>
	new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => {
			const resultado = reader.result;
			if (typeof resultado !== 'string') {
				reject(new Error('Falha ao converter arquivo para base64.'));
				return;
			}
			const base64 = resultado.includes(',') ? resultado.split(',')[1] : resultado;
			resolve(base64);
		};
		reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler arquivo.'));
		reader.readAsDataURL(arquivo);
	});

const validarArquivoPdfOuDocx = (arquivo: File): string | null => {
	const nome = (arquivo.name || '').toLowerCase();
	const okExt = nome.endsWith('.pdf') || nome.endsWith('.docx');
	const okMime =
		arquivo.type === MIME_PDF ||
		arquivo.type === MIME_DOCX ||
		arquivo.type === '' ||
		arquivo.type === 'application/octet-stream';
	if (!okExt) {
		return 'Selecione um arquivo PDF ou DOCX.';
	}
	if (!okMime) {
		return 'Tipo de arquivo não permitido. Use PDF ou DOCX.';
	}
	return null;
};

const formatarDataParaJson = (valor: any) => {
	if (!valor) return null;
	const data = valor instanceof Date ? valor : new Date(valor);
	return Number.isNaN(data.getTime()) ? null : data.toISOString();
};

const parseDate = (valor: any) => {
	if (!valor) return null;
	const data = new Date(valor);
	return Number.isNaN(data.getTime()) ? null : data;
};

const normalizarPrevisaoLpmaParaFormulario = (valor: any): string => {
	if (valor === true || valor === 'true') return 'true';
	if (valor === false || valor === 'false') return 'false';
	return '';
};

const parseJsonArray = (valor: any): any[] => {
	if (!valor) return [];
	if (Array.isArray(valor)) return valor;
	if (typeof valor === 'string') {
		try {
			const parsed = JSON.parse(valor);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}
	return [];
};

export default function PlantioOutraCulturaModal({
	show,
	onHide,
	onSubmit,
	dadosPlantioOutraCultura,
	cadernoLpmaId,
	assinaturasDisponiveis = [],
}: PlantioOutraCulturaModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		setValue,
		getValues,
		watch,
	} = useForm<PlantioOutraCulturaForm>({
		mode: 'all',
		defaultValues,
		shouldUnregister: false,
	});

	const [linhasLimpeza, setLinhasLimpeza] = useState<number>(1);
	const [valoresTransformados, setValoresTransformados] = useState<Record<string, any> | null>(null);
	const [assinaturaEnvolvidos, setAssinaturaEnvolvidos] = useState<string | null>(null);
	const [assinaturaUsuario, setAssinaturaUsuario] = useState<string | null>(null);
	const [mostrarAssinatura, setMostrarAssinatura] = useState(false);
	const [nomesAdicionais, setNomesAdicionais] = useState<string[]>([]);
	const [arquivoComunicacaoAutorizacao, setArquivoComunicacaoAutorizacao] = useState<File | null>(null);
	const [erroArquivoComunicacaoAutorizacao, setErroArquivoComunicacaoAutorizacao] = useState('');
	const [removerAnexoComunicacaoAutorizacao, setRemoverAnexoComunicacaoAutorizacao] = useState(false);

	const idPlantioEdicao =
		dadosPlantioOutraCultura?.id ?? (dadosPlantioOutraCultura as { Id?: number } | undefined)?.Id ?? 0;
	const idCadernoParaDownload =
		cadernoLpmaId ??
		dadosPlantioOutraCultura?.cadernoLPMAId ??
		(dadosPlantioOutraCultura as { CadernoLPMAId?: number } | undefined)?.CadernoLPMAId ??
		0;

	const baixarAnexoComunicacaoAutorizacao = async () => {
		if (!idCadernoParaDownload || !idPlantioEdicao) {
			return;
		}
		try {
			const resposta = await api.get(
				`api/v1/cadernoLPMA/cadernoLPMA/${idCadernoParaDownload}/plantios-outra-cultura/${idPlantioEdicao}/comunicacao-autorizacao`,
				{ responseType: 'blob' },
			);
			const nome =
				dadosPlantioOutraCultura?.comunicacaoAutorizacaoNomeArquivo ??
				`comunicacao-autorizacao-${idPlantioEdicao}`;
			const contentType =
				dadosPlantioOutraCultura?.comunicacaoAutorizacaoContentType ?? 'application/octet-stream';
			const blob = new Blob([resposta.data], { type: contentType });
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = nome;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Erro ao baixar anexo:', error);
		}
	};

	useEffect(() => {
		const carregarAssinaturaUsuario = async () => {
			try {
				const token = localStorage.getItem('token');
				if (token) {
					const decoded: any = JSON.parse(atob(token.split('.')[1]));
					const username = decoded.unique_name || '';

					const { data } = await api.get(`/api/v1/admin/usuarios/assinatura/${username}`);
					if (data && data.assinaturaBase64) {
						setAssinaturaUsuario(data.assinaturaBase64);
					}
				}
			} catch (error) {
				console.error('Erro ao carregar assinatura:', error);
			}
		};

		if (show) {
			carregarAssinaturaUsuario();
			setAssinaturaEnvolvidos(dadosPlantioOutraCultura?.assinaturaEnvolvidosBase64 || null);
			setNomesAdicionais(parseNomesEnvolvidosJson(dadosPlantioOutraCultura?.nomesEnvolvidosAdicionaisJson));
		} else {
			setAssinaturaEnvolvidos(null);
			setMostrarAssinatura(false);
			setNomesAdicionais([]);
		}
	}, [
		show,
		dadosPlantioOutraCultura?.assinaturaEnvolvidosBase64,
		dadosPlantioOutraCultura?.nomesEnvolvidosAdicionaisJson,
	]);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setLinhasLimpeza(1);
			setValoresTransformados(null);
			setArquivoComunicacaoAutorizacao(null);
			setErroArquivoComunicacaoAutorizacao('');
			setRemoverAnexoComunicacaoAutorizacao(false);
			return;
		}

		if (!dadosPlantioOutraCultura) {
			reset(defaultValues);
			setLinhasLimpeza(1);
			setValoresTransformados(null);
			setArquivoComunicacaoAutorizacao(null);
			setErroArquivoComunicacaoAutorizacao('');
			setRemoverAnexoComunicacaoAutorizacao(false);
			return;
		}

		setArquivoComunicacaoAutorizacao(null);
		setErroArquivoComunicacaoAutorizacao('');
		setRemoverAnexoComunicacaoAutorizacao(false);

		const plantios = parseJsonArray(dadosPlantioOutraCultura.plantioOutraCulturaJson);
		const limpezas = parseJsonArray(dadosPlantioOutraCultura.limpezaPlantioOutraCulturaJson);

		const dadosParaReset: Record<string, any> = {
			...dadosPlantioOutraCultura,
			dataInicioPlantioOutraCultura: parseDate(dadosPlantioOutraCultura.dataInicioPlantioOutraCultura),
			dataTerminoPlantioOutraCultura: parseDate(dadosPlantioOutraCultura.dataTerminoPlantioOutraCultura),
			talhaoPlantioOutraCultura:
				dadosPlantioOutraCultura.talhaoPlantioOutraCultura ?? plantios[0]?.talhao ?? '',
			culturaPlantadaPlantioOutraCultura:
				dadosPlantioOutraCultura.culturaPlantadaPlantioOutraCultura ?? plantios[0]?.cultura ?? '',
			dataAutorizacao: parseDate(dadosPlantioOutraCultura.dataAutorizacao),
			previsaoLPMA: normalizarPrevisaoLpmaParaFormulario(dadosPlantioOutraCultura.previsaoLPMA),
		};

		limpezas.forEach((item: any, index: number) => {
			const metodoLimpeza = normalizarMetodoLimpezaParaFormulario(item?.metodo);
			dadosParaReset[`limpezaNome${index}`] = item?.nome || '';
			dadosParaReset[`limpezaDataEntrada${index}`] = parseDate(item?.dataAntes ?? item?.dataEntrada);
			dadosParaReset[`limpezaDataSaida${index}`] = parseDate(item?.dataDepois ?? item?.dataSaida);
			dadosParaReset[`limpezaMetodo${index}`] = metodoLimpeza.metodo;
			dadosParaReset[`limpezaMetodoOutros${index}`] = metodoLimpeza.outros;
			dadosParaReset[`limpezaResponsavel${index}`] = item?.responsavel || '';
		});

		setLinhasLimpeza(Math.max(1, limpezas.length));
		setValoresTransformados(dadosParaReset);
	}, [show, dadosPlantioOutraCultura, reset]);

	useEffect(() => {
		if (valoresTransformados && linhasLimpeza > 0) {
			reset(valoresTransformados as PlantioOutraCulturaForm);
		}
	}, [linhasLimpeza, valoresTransformados, reset]);

	const adicionarLinhaLimpeza = () => setLinhasLimpeza((prev) => prev + 1);
	const removerLinhaLimpeza = (index: number) => {
		if (linhasLimpeza === 1) return;
		setLinhasLimpeza((prev) => prev - 1);
		setValue(`limpezaNome${index}` as any, '');
		setValue(`limpezaDataEntrada${index}` as any, null);
		setValue(`limpezaDataSaida${index}` as any, null);
		setValue(`limpezaMetodo${index}` as any, '');
		setValue(`limpezaMetodoOutros${index}` as any, '');
		setValue(`limpezaResponsavel${index}` as any, '');
	};

	const handleSubmitInterno = async (dados: PlantioOutraCulturaForm) => {
		if (erroArquivoComunicacaoAutorizacao) {
			return;
		}

		const valores = getValues() as Record<string, any>;
		const dadosNormalizados: Record<string, any> = { ...dados };

		const registrosLimpeza: any[] = [];
		for (let i = 0; i < linhasLimpeza; i++) {
			const nome = valores[`limpezaNome${i}`];
			const dataEntrada = valores[`limpezaDataEntrada${i}`];
			const dataSaida = valores[`limpezaDataSaida${i}`];
			const metodo = formatarMetodoLimpezaParaJson(
				valores[`limpezaMetodo${i}`],
				valores[`limpezaMetodoOutros${i}`],
			);
			const responsavel = valores[`limpezaResponsavel${i}`];

			if (nome || dataEntrada || dataSaida || metodo || responsavel) {
				registrosLimpeza.push({
					nome,
					dataAntes: formatarDataParaJson(dataEntrada),
					dataDepois: formatarDataParaJson(dataSaida),
					metodo,
					responsavel,
				});
			}

			delete dadosNormalizados[`limpezaNome${i}`];
			delete dadosNormalizados[`limpezaDataEntrada${i}`];
			delete dadosNormalizados[`limpezaDataSaida${i}`];
			delete dadosNormalizados[`limpezaMetodo${i}`];
			delete dadosNormalizados[`limpezaMetodoOutros${i}`];
			delete dadosNormalizados[`limpezaResponsavel${i}`];
		}

		dadosNormalizados.plantioOutraCulturaJson = null;
		dadosNormalizados.limpezaPlantioOutraCulturaJson = registrosLimpeza.length ? JSON.stringify(registrosLimpeza) : null;
		dadosNormalizados.assinaturaEnvolvidosBase64 = assinaturaEnvolvidos || null;
		dadosNormalizados.nomesEnvolvidosAdicionaisJson = stringifyNomesEnvolvidos(nomesAdicionais);

		if (removerAnexoComunicacaoAutorizacao) {
			dadosNormalizados.removerComunicacaoAutorizacaoAnexo = true;
			dadosNormalizados.comunicacaoAutorizacaoConteudoBase64 = null;
			dadosNormalizados.comunicacaoAutorizacaoNomeArquivo = null;
			dadosNormalizados.comunicacaoAutorizacaoContentType = null;
			dadosNormalizados.comunicacaoAutorizacaoCaminhoBlob = null;
		} else if (arquivoComunicacaoAutorizacao) {
			dadosNormalizados.comunicacaoAutorizacaoConteudoBase64 = await arquivoParaBase64(arquivoComunicacaoAutorizacao);
			dadosNormalizados.comunicacaoAutorizacaoNomeArquivo = arquivoComunicacaoAutorizacao.name;
			dadosNormalizados.comunicacaoAutorizacaoContentType =
				arquivoComunicacaoAutorizacao.type || MIME_DOCX;
			dadosNormalizados.removerComunicacaoAutorizacaoAnexo = false;
		} else {
			dadosNormalizados.comunicacaoAutorizacaoNomeArquivo =
				dadosPlantioOutraCultura?.comunicacaoAutorizacaoNomeArquivo ?? null;
			dadosNormalizados.comunicacaoAutorizacaoContentType =
				dadosPlantioOutraCultura?.comunicacaoAutorizacaoContentType ?? null;
			dadosNormalizados.comunicacaoAutorizacaoCaminhoBlob =
				dadosPlantioOutraCultura?.comunicacaoAutorizacaoCaminhoBlob ?? null;
			dadosNormalizados.comunicacaoAutorizacaoConteudoBase64 = null;
			dadosNormalizados.removerComunicacaoAutorizacaoAnexo = false;
		}

		onSubmit(dadosNormalizados as CadernoLPMAPlantioOutraCultura);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.PLANTIOOUTRACULTURA_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(handleSubmitInterno)} autoComplete="off">

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Registro de acesso dos envolvidos à área da LPMA e datas inicial/final de realização das atividades</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom">
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data inicial</Form.Label>
							<Controller
								control={control}
								name="dataInicioPlantioOutraCultura"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Data final</Form.Label>
							<Controller
								control={control}
								name="dataTerminoPlantioOutraCultura"
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Talhão</Form.Label>
							<Form.Control
								ref={register}
								name="talhaoPlantioOutraCultura"
								placeholder="Talhão"
								maxLength={150}
							/>
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group>
							<Form.Label>Cultura plantada</Form.Label>
							<Form.Control
								ref={register}
								name="culturaPlantadaPlantioOutraCultura"
								placeholder="Cultura plantada"
								maxLength={150}
							/>
						</Form.Group>
					</Col>
				</Row>

				<AssinaturaComNomesAdicionais
					label="assinatura"
					assinaturaUsuario={assinaturaUsuario}
					assinaturaBase64={assinaturaEnvolvidos}
					onAssinaturaBase64Change={setAssinaturaEnvolvidos}
					nomesAdicionais={nomesAdicionais}
					onNomesAdicionaisChange={setNomesAdicionais}
					mostrarAssinatura={mostrarAssinatura}
					onMostrarAssinaturaChange={setMostrarAssinatura}
					assinaturasDisponiveis={assinaturasDisponiveis}
				/>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de plantio de outra cultura na área da LPMA</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>É previsto na LPMA / Parecer Técnico o plantio de outra cultura na área da LPMA?</Form.Label>
						<div className="d-flex">
							<Form.Check
								type="radio"
								ref={register}
								name="previsaoLPMA"
								value="true"
								label="Sim"
								className="mr-3"
							/>
							<Form.Check
								type="radio"
								ref={register}
								name="previsaoLPMA"
								value="false"
								label="Não"
							/>
						</div>
						<p className="mt-2 mb-2">
							<small>
								* Somente culturas sexualmente incompatíveis com a cultura objeto da LPMA podem ser plantadas na área.
								As sementes oriundas deste plantio não poderão ser utilizadas para multiplicação futura.
							</small>
						</p>
						<p className="mb-2">
							<small>
								Em caso negativo, a CIBio autorizou o referido plantio em:
							</small>
						</p>
						<Controller
							control={control}
							name="dataAutorizacao"
							render={({ onChange, value }) => (
								<DatePicker
									selected={value || null}
									onChange={onChange}
									className="input-custom-to-date"
									locale="pt-BR"
									dateFormat="dd/MM/yyyy"
									placeholderText="Selecionar a data"
								/>
							)}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom mt-3">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de limpeza de maquinários / equipamentos utilizados no plantio</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={4}>Nome e código do maquinário / equipamento</Col>
					<Col md={2}>Data de limpeza – Antes (entrada)</Col>
					<Col md={2}>Data de limpeza – Antes (saída)</Col>
					<Col md={2}>Método de limpeza</Col>
					<Col md={2}>Responsável</Col>
				</Row>

				{Array.from({ length: linhasLimpeza }).map((_, index) => {
					const metodoSelecionado = watch(`limpezaMetodo${index}` as any);
					return (
					<Row className="row-custom align-items-end" key={`limpeza-plantio-${index}`}>
						<Col md={4}>
							<Form.Control ref={register} name={`limpezaNome${index}`} placeholder="Maquinário / equipamento" maxLength={150} />
						</Col>
						<Col md={2}>
							<Controller
								control={control}
								name={`limpezaDataEntrada${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md={2}>
							<Controller
								control={control}
								name={`limpezaDataSaida${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md={2}>
							<MetodoLimpezaSelect
								register={register}
								name={`limpezaMetodo${index}`}
								nameOutros={`limpezaMetodoOutros${index}`}
								metodoSelecionado={metodoSelecionado}
							/>
						</Col>
						<Col md={2}>
							<Form.Control ref={register} name={`limpezaResponsavel${index}`} placeholder="Responsável" maxLength={150} />
						</Col>
						<Col md="auto" className="d-flex justify-content-end">
							{linhasLimpeza > 1 && (
								<Button title="Remover" contained={false} type="button" onClick={() => removerLinhaLimpeza(index)} />
							)}
						</Col>
					</Row>
					);
				})}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar maquinário" contained={false} type="button" onClick={adicionarLinhaLimpeza} />
				</div>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações e informações adicionais</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesPlantioOutraCultura"
							placeholder="Registre observações adicionais sobre o plantio da outra cultura"
							maxLength={500}
						/>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Comunicação / autorização (PDF ou DOCX)</Form.Label>
						<div style={{ maxWidth: 520 }}>
							<Form.Control
								type="file"
								accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
								onChange={(e) => {
									const input = e.target as HTMLInputElement;
									const f = input.files?.[0];
									setErroArquivoComunicacaoAutorizacao('');
									setRemoverAnexoComunicacaoAutorizacao(false);
									if (!f) {
										setArquivoComunicacaoAutorizacao(null);
										return;
									}
									const err = validarArquivoPdfOuDocx(f);
									if (err) {
										setErroArquivoComunicacaoAutorizacao(err);
										setArquivoComunicacaoAutorizacao(null);
										input.value = '';
										return;
									}
									setArquivoComunicacaoAutorizacao(f);
								}}
							/>
							{erroArquivoComunicacaoAutorizacao ? (
								<small className="text-danger d-block mt-1">{erroArquivoComunicacaoAutorizacao}</small>
							) : null}
							{arquivoComunicacaoAutorizacao ? (
								<small className="d-block text-muted mt-1">
									Selecionado: {arquivoComunicacaoAutorizacao.name}
								</small>
							) : null}
							{!arquivoComunicacaoAutorizacao &&
							dadosPlantioOutraCultura?.comunicacaoAutorizacaoNomeArquivo &&
							!removerAnexoComunicacaoAutorizacao ? (
								<div className="mt-2">
									<small className="text-muted">
										Arquivo atual: {dadosPlantioOutraCultura.comunicacaoAutorizacaoNomeArquivo}
									</small>
									{idCadernoParaDownload > 0 && idPlantioEdicao > 0 ? (
										<>
											{' '}
											<Button
												type="button"
												contained={false}
												title="Baixar"
												onClick={() => void baixarAnexoComunicacaoAutorizacao()}
											/>
										</>
									) : null}
									{' '}
									<Button
										type="button"
										contained={false}
										title="Remover anexo"
										onClick={() => {
											setRemoverAnexoComunicacaoAutorizacao(true);
											setArquivoComunicacaoAutorizacao(null);
										}}
									/>
								</div>
							) : null}
							{removerAnexoComunicacaoAutorizacao ? (
								<small className="text-warning d-block mt-1">O anexo será removido ao salvar.</small>
							) : null}
						</div>
					</Form.Group>
				</Form.Row>

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

